import type { SurveyAnswerValue } from './answer.type'
import type { QuestionType } from './logic/survey-logic-json.type'
import { z } from '../utils/zod-extend'

type QuestionChoiceTypeString = string
type QuestionChoiceTypeObjectBase = {
  value: string
  text: string
}

/**
 * {
 *   value: '01J6VFYSEKRJYSM91ZWW0Z2T3M',
 *   text: '選択肢1-label',
 *   productId: '01J6VFYSEKRJYSM91ZWW0Z2T3M',
 * }
 */
export type QuestionChoiceTypeObject = QuestionChoiceTypeObjectBase & {
  productId?: string
}

/**
 * radiogroup, checkbox, rankingのchoicesに入る型
 * 注意 : imagepickerのchoicesは違う型
 */
export type QuestionChoiceType = QuestionChoiceTypeString | QuestionChoiceTypeObject

type QuestionBaseModelData = {
  type: QuestionType
  name: string // unique key
  title?: string
  isRequired?: boolean
  defaultValue?: SurveyAnswerValue
  description?: string
  visible?: boolean
}

export type QuestionSubModelNoneAndOtherSelectable = {
  showNoneItem?: boolean
  showOtherItem?: boolean
  noneText?: string
  otherText?: string
}

type QuestionSubModelCommentable = {
  showCommentArea?: boolean // コメント領域の表示を制御
  commentText?: string // コメント領域のテキスト
  commentPlaceholder?: string // コメント領域のプレースホルダ
}

export type ValidationType = 'none' | 'alphanumericWithSymbols' | 'alphanumericOnly'

export type QuestionTextModelData = QuestionBaseModelData & {
  type: 'text'
  placeholder?: string
  validationType?: ValidationType
}

export type QuestionNumberModelData = QuestionBaseModelData & {
  type: 'number'
  placeholder?: string
  min?: number
  max?: number
  step?: number
}

export type QuestionDateModelData = QuestionBaseModelData & {
  type: 'date'
  placeholder?: string
  min?: string
  max?: string
}

export type QuestionCommentModelData = QuestionBaseModelData & {
  type: 'comment'
  placeholder?: string
  validationType?: ValidationType
}

export type QuestionBooleanModelData
  = QuestionBaseModelData
    & QuestionSubModelCommentable
    & {
  type: 'boolean'
  labelTrue?: string // 「はい」にカスタムラベルを入力した時
  labelFalse?: string // 「いいえ」にカスタムラベルを入力した時
  swapOrder?: boolean // 「はい」「いいえ」の順番切り替え
}

export const fileQuestionUsageSchema = z.enum(['normalImage', 'receiptImage', 'barcodeImage'])
export type FileQuestionUsage = z.infer<typeof fileQuestionUsageSchema>

export type QuestionFileModelData
  = QuestionBaseModelData
    & QuestionSubModelCommentable
    & {
  type: 'file'
  acceptedTypes: string[]
  storeDataAsText?: boolean
  waitForUpload?: boolean
  maxSize?: number
  filePlaceholder?: string
  photoPlaceholder?: string
  fileOrPhotoPlaceholder?: string
  usage?: FileQuestionUsage
}

export type QuestionRatingModelData
  = QuestionBaseModelData
    & QuestionSubModelCommentable
    & {
  type: 'rating'
  autoGenerate?: boolean
  rateCount?: number
  rateMin?: number
  rateMax?: number
  rateStep?: number
  rateValues?: (number | {
    value: number
    text: string
  })[]
  rateType?: 'stars' | 'labels'
}

export type QuestionRadiogroupModelData
  = QuestionBaseModelData
    & QuestionSubModelNoneAndOtherSelectable
    & QuestionSubModelCommentable
    & {
  type: 'radiogroup'
  choices: QuestionChoiceType[]
}

export type QuestionCheckboxModelData
  = QuestionBaseModelData
    & QuestionSubModelNoneAndOtherSelectable
    & QuestionSubModelCommentable
    & {
  type: 'checkbox'
  choices: QuestionChoiceType[]
  showSelectAllItem?: boolean
  selectAllText?: string
}

export type QuestionDropdownModelData
  = QuestionBaseModelData
    & QuestionSubModelNoneAndOtherSelectable
    & QuestionSubModelCommentable
    & {
  type: 'dropdown'
  choices: QuestionChoiceType[]
  optionsCaption?: string
}

export type QuestionRankingModelData
  = QuestionBaseModelData
    & QuestionSubModelCommentable
    & {
  type: 'ranking'
  choices: QuestionChoiceType[]
}

export type QuestionImagepickerModelDataChoice = {
  value: string
  text?: string
  imageLink: string
}

/**
 * 質問を作成しただけでは、choicesは undefined になるので注意
 */
export type QuestionImagepickerModelData
  = QuestionBaseModelData
    & QuestionSubModelCommentable
    & {
  type: 'imagepicker'
  choices?: QuestionImagepickerModelDataChoice[]
  showLabel?: boolean // creatorでcaption入力を制御 // 現在固定でtrueにしている
  showCaption?: boolean // ユーザー側のcaption表示を制御
  displayColumnSize?: 'one' | 'two' // 1カラム or 2カラム
  multiSelect?: boolean // 複数選択を許可するか
}

export type QuestionPanelModelData = QuestionBaseModelData & {
  type: 'panel'
  elements?: SurveyElementJson[]
}

export type ChoicesType = QuestionRadiogroupModelData['choices']
  | QuestionCheckboxModelData['choices']
  | QuestionImagepickerModelData['choices']
  | QuestionRatingModelData['rateValues']

const surveyElementJsonSchema = z.union([
  z.custom<QuestionRatingModelData>(),
  z.custom<QuestionRadiogroupModelData>(),
  z.custom<QuestionBooleanModelData>(),
  z.custom<QuestionTextModelData>(),
  z.custom<QuestionCommentModelData>(),
  z.custom<QuestionCheckboxModelData>(),
  z.custom<QuestionImagepickerModelData>(),
  z.custom<QuestionFileModelData>(),
  z.custom<QuestionNumberModelData>(),
  z.custom<QuestionDateModelData>(),
  z.custom<QuestionPanelModelData>(),
  z.custom<QuestionRankingModelData>(),
  z.custom<QuestionDropdownModelData>(),
])

export type SurveyElementJson = z.output<typeof surveyElementJsonSchema>

export const surveyConfigSchema = z.object({
  logoPosition: z.literal('right').optional(),
  elements: z.array(surveyElementJsonSchema).optional(),
}).openapi({
  example: {
    logoPosition: 'right',
    elements: [
      {
        type: 'checkbox',
        name: '質問1',
        title: 'aaaa',
        choices: ['Item 1', 'Item 2', 'Item 3'],
      },
    ],
  },
})

// export type SurveyConfig = z.infer<typeof surveyConfigSchema>
export type SurveyConfig = {
  logoPosition?: 'right' // 使ってないけど "right"が保存されてる
  elements?: SurveyElementJson[]
}

// FIXME: 元は survey-config-migration.type.ts にあったが、survey-json.type.ts に移動
export type SurveyConfigTodoReplace = Record<string, any> & {
  elements: Array<unknown>
}
